import { Text, useModalState } from "@clipboard-health/ui-components";
import InfoIcon from "@mui/icons-material/Info";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
} from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { MIN_SHIFT_LENGTH_FOR_MANDATORY_BREAK_IN_MINUTES } from "@src/appV2/redesign/Shift/constants";
import { type SyntheticEvent } from "react";

import { getHumanReadableShiftDuration } from "../../redesign/ShiftDiscovery/Blocks/utils";

interface MandatoryLunchInformation {
  facilityName: string;
  shiftTime: number;
  mandatoryBreakDurationInMinutes: number;
}

interface LunchBreakInformationIconAndDialogProps {
  shiftDetails: MandatoryLunchInformation;
}
export function LunchBreakInformationIconAndDialog(props: LunchBreakInformationIconAndDialogProps) {
  const { shiftDetails } = props;
  const lunchBreakDialogState = useModalState();
  const isMandatoryLunchBreakInfoPopupEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_LUNCH_BREAK_INFO_POPUP,
    {
      defaultValue: false,
    }
  );

  return (
    <>
      {isMandatoryLunchBreakInfoPopupEnabled &&
      shiftDetails.shiftTime > MIN_SHIFT_LENGTH_FOR_MANDATORY_BREAK_IN_MINUTES / 60 ? (
        <IconButton
          size="small"
          edge="end"
          aria-label="Mandatory Lunch Break Button"
          sx={{
            paddingRight: 0,
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            lunchBreakDialogState.openModal();
          }}
        >
          <InfoIcon color="primary" fontSize="small" />
        </IconButton>
      ) : null}
      <Dialog
        open={lunchBreakDialogState.modalIsOpen}
        onClose={(event) => {
          (event as SyntheticEvent)?.stopPropagation?.();
          lunchBreakDialogState.closeModal();
        }}
      >
        <DialogTitle>
          <Text bold align="center">
            {shiftDetails.facilityName} requires a mandatory{" "}
            {shiftDetails.mandatoryBreakDurationInMinutes} minute break!
          </Text>
        </DialogTitle>
        <DialogContent>
          <DialogContentText align="center">
            The full duration for each shift in this block is{" "}
            {getHumanReadableShiftDuration({
              shiftDuration: shiftDetails.shiftTime,
              withLunchBreak: true,
              mandatoryBreakDurationInMinutes: shiftDetails.mandatoryBreakDurationInMinutes,
            })}
            . Your pay for each shift will reflect that you worked{" "}
            {getHumanReadableShiftDuration({
              shiftDuration: shiftDetails.shiftTime,
              withLunchBreak: false,
              mandatoryBreakDurationInMinutes: shiftDetails.mandatoryBreakDurationInMinutes,
            })}{" "}
            because a {shiftDetails.mandatoryBreakDurationInMinutes} minutes unpaid break is
            mandatory at this facility.
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ padding: 0 }}>
          <Button
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              lunchBreakDialogState.closeModal();
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
